import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";
import type { HousingProduct, KITProductData } from "~/domains/product/types/ProductData";
import { Accordion, AccordionDetails, AccordionSummary, Card, Divider, Grid, Stack, styled } from "~/common/mui";
import { useTranslation } from "react-i18next";
import type { LineItem, PlainLineItem } from "~/features/cart/cart/models/CartData";
import {
  ContractItemParam,
  QuoteItemParam,
  userCanSeePriceReference,
} from "~/features/cart/cart/components/LineItemStaticParams";
import { PriceElements } from "~/domains/pricing/components/Price/PriceV4";
import { Parameter } from "~/common/components/ParameterV2";
import { getEDDLabel } from "~/features/cart/cart/utils/cartUtils";
import { useViewableLineItemData } from "~/domains/cart/hooks";
import { LineItemMessagesSection } from "~/domains/cart/components";
import { ViewablePackagingCell } from "~/features/cart/cart/components/PackagingCell";
import { LineItemStockIndicator } from "~/features/cart/cart/components/LineItemStockIndicator";
import { NumericFormat } from "react-number-format";
import { NumberFormatter as NF } from "~/common/utils/NumberFormatter";
import { ProductParams2 } from "~/domains/product/components/ProductParamsV2";
import { PartialDeliveriesSection } from "./PartialDeliveriesSection";
import { IncludedSurchargeAsterisk, TariffSurchargeAsterisk, OptionalBox } from "./commonComponents";
import { isLineLevelCustomerOrderNumberEnabled } from "~/utils/features";
import { FormStaticText } from "~/common/components/FormStaticText/FormStaticText";
import { useSelector } from "react-redux";
import { selectSubLineItems } from "../cart/redux/selectors/cartSelectors";
import { getTotalPrice } from "~/domains/product/utils/productDescriptorUtils";
import { text, volatileSum } from "~/utils/PriceUtils";
import { OptionalPartEntry } from "./OptionalPartEntry";
import { Product } from "~/domains/product/Product";
import { ProductLineGrid } from "./ProductLineGrid";

interface CheckoutLineItemProps {
  className?: string;
  lineItem: LineItem | PlainLineItem;
  product: ProductDescriptor | ProductDescriptor<KITProductData>;
}

interface IncludedPartTileProps {
  housingProduct: HousingProduct;
  kitQuantity: number;
}

const IncludedPartTile = ({ housingProduct, kitQuantity }: IncludedPartTileProps) => {
  return (
    <Product.Context value={housingProduct.product}>
      <Card component={Stack} direction={"row"} gap={2} sx={{ p: 2, height: "100%" }}>
        <Product.PrimaryImageWithLogosV2 size={64} gap={8} />
        <Stack gap={1}>
          <Product.DesignationV2 fontWeight={"bold"} />
          <Parameter label="Quantity" value={housingProduct.quantity * kitQuantity} />
        </Stack>
      </Card>
    </Product.Context>
  );
};

const TotalPrice = styled("strong")({
  fontWeight: "bold",
});

export function CheckoutProductLine({ className, lineItem, product }: CheckoutLineItemProps) {
  const { t } = useTranslation();

  const isKit = "includedParts" in product.base;

  const { pricing, flags, formatted } = useViewableLineItemData({ lineItem, product });

  const { quantityVisible, isPMA, isOptime, webservicesDisabled } = flags;
  const { totalPriceFormatted, hasSurcharge, surchargeValue, tariffSurcharge, zeroPriceLabel } = pricing;

  const showPriceReference = userCanSeePriceReference();
  const showEdd = true;

  const aliases = ProductParams2.ProductAliases({
    mode: "15-digits",
    visibleAliasesLayout: "separate-rows",
    packCode: lineItem.packCode,
  })({
    t,
    product,
  });

  const gpsd = ProductParams2.GPSD({
    t,
    product,
  });

  const optionalPartLineItems = useSelector(selectSubLineItems(lineItem.id));

  let kitAccordionSlot: React.ReactNode = undefined;

  if (isKit) {
    const kitProduct = product as ProductDescriptor<KITProductData>;
    const hasIncludedParts = kitProduct.base.includedParts.length > 0;
    const incluedPartsTotalQty = kitProduct.base.includedParts.reduce(
      (sum, part) => sum + part.quantity * lineItem.quantity,
      0,
    );

    const optionalPartsTotalQty = optionalPartLineItems.reduce((sum, item) => sum + item.quantity, 0);
    const optionalPartsTotalValue = volatileSum(
      ...optionalPartLineItems.map((item) => getTotalPrice(item.netPrice, item.quantity)),
    );

    const selectedParts = kitProduct.base.optionalParts
      .map((part) => ({
        part,
        lineItem: optionalPartLineItems.find((item) => item.productCode === part.product.code),
      }))
      .filter((item) => item.lineItem) as {
      part: HousingProduct;
      lineItem: LineItem;
    }[];

    kitAccordionSlot = (
      <Card sx={{ bgcolor: "surface.main" }}>
        {hasIncludedParts && (
          <>
            <Accordion
              sx={(theme) => ({
                "&::before": { display: "none !important" },
                "&::after": { display: "none !important" },
                bgcolor: "common.white",
                transition: theme.transitions.create("background-color"),
                "&.Mui-expanded": {
                  bgcolor: "surface.main",
                },
              })}
            >
              <AccordionSummary>
                <Stack gap={1}>
                  {t("page.cart.housing.included.title")}
                  <Stack direction="row" gap={2} sx={{ fontWeight: "normal" }}>
                    <Parameter
                      variant="row"
                      label={t("cartPage.table.item", { count: incluedPartsTotalQty })}
                      value={incluedPartsTotalQty}
                    />
                  </Stack>
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container columns={{ phone: 2, "desktop-m": 4 }} gap={2}>
                  {kitProduct.base.includedParts.map((part) => (
                    <Grid key={part.product.designation} size={1}>
                      <IncludedPartTile housingProduct={part} kitQuantity={lineItem.quantity} />
                    </Grid>
                  ))}
                </Grid>
              </AccordionDetails>
            </Accordion>
            <Divider flexItem />
          </>
        )}

        <Accordion
          sx={(theme) => ({
            "&::before": { display: "none !important" },
            "&::after": { display: "none !important" },
            bgcolor: "common.white",
            transition: theme.transitions.create("background-color"),
            "&.Mui-expanded": {
              bgcolor: "surface.main",
            },
          })}
        >
          <AccordionSummary>
            <Stack gap={1}>
              {t("page.cart.housing.optional.title")}
              <Stack direction="row" gap={2} sx={{ fontWeight: "normal" }}>
                <Parameter
                  variant="row"
                  label={t("cartPage.table.item", { count: optionalPartsTotalQty })}
                  value={optionalPartsTotalQty}
                />
                <Parameter
                  variant="row"
                  label={t("cartPage.table.header.total.title")}
                  value={<strong>{text(optionalPartsTotalValue)}</strong>}
                />
              </Stack>
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <Stack gap={2}>
              {selectedParts.map(({ part, lineItem: optionalItem }) => (
                <OptionalPartEntry key={part.designation} product={part} lineItem={optionalItem} editMode={false} />
              ))}
            </Stack>
          </AccordionDetails>
        </Accordion>
      </Card>
    );
  }

  return (
    <Card sx={{ p: 3 }} component={Stack} gap={3} className={className}>
      <ProductLineGrid>
        <ProductLineGrid.StockIndicator>
          <LineItemStockIndicator product={product} packEntry={lineItem} />
        </ProductLineGrid.StockIndicator>

        <ProductLineGrid.Quantity>
          <Parameter
            label={t("input.quantity")}
            value={
              <NumericFormat
                value={formatted.quantity}
                displayType="text"
                thousandSeparator={NF.getThousandSeparator()}
                decimalSeparator={NF.getDecimalSeparator()}
              />
            }
          />
        </ProductLineGrid.Quantity>

        <ProductLineGrid.NetPrice>
          <Parameter
            label={<PriceElements.NetPriceLabel variant="inherit" price={lineItem.netPrice} />}
            value={
              webservicesDisabled ? null : (
                <PriceElements.Stack gap={0}>
                  <PriceElements.Price
                    variant="inherit"
                    price={lineItem.netPrice}
                    zeroText={zeroPriceLabel}
                    tariffSurcharge={tariffSurcharge}
                  />
                </PriceElements.Stack>
              )
            }
          />
        </ProductLineGrid.NetPrice>

        <ProductLineGrid.TotalPrice>
          <Parameter
            label={<PriceElements.TotalPriceLabel variant="inherit" price={lineItem.netPrice} />}
            value={
              <Stack>
                {!webservicesDisabled && (
                  <TotalPrice>
                    {totalPriceFormatted}
                    {hasSurcharge && "*"}
                  </TotalPrice>
                )}
              </Stack>
            }
          />
        </ProductLineGrid.TotalPrice>

        <ProductLineGrid.DeliveryDate>
          {quantityVisible && !isPMA ? (
            <FormStaticText
              label={t("cartPage.table.header.delivery.title")}
              value={formatted.deliveryDate}
              helperText={showEdd ? getEDDLabel(lineItem, t) : "-"}
            />
          ) : (
            <FormStaticText label={t("cartPage.table.header.delivery.title")} value={"-"} />
          )}
        </ProductLineGrid.DeliveryDate>

        <ProductLineGrid.Packaging>
          <ViewablePackagingCell lineItem={lineItem} product={product} />
        </ProductLineGrid.Packaging>

        {showPriceReference && (
          <ProductLineGrid.PriceReference>
            <Parameter label={t("cartPage.table.header.priceReference.title")} value={lineItem.priceReference || "-"} />
          </ProductLineGrid.PriceReference>
        )}

        {aliases && (
          <ProductLineGrid.Aliases>
            <Parameter {...aliases} />
          </ProductLineGrid.Aliases>
        )}

        {gpsd && (
          <ProductLineGrid.GPSD>
            <Parameter {...gpsd} />
          </ProductLineGrid.GPSD>
        )}

        <ProductLineGrid.QuoteDetails>
          <QuoteItemParam product={product.base} lineItem={lineItem} />
        </ProductLineGrid.QuoteDetails>

        <ProductLineGrid.ContractDetails>
          <ContractItemParam product={product.base} lineItem={lineItem} />
        </ProductLineGrid.ContractDetails>

        {isLineLevelCustomerOrderNumberEnabled() && lineItem.customerOrderNumber && (
          <ProductLineGrid.PONumber>
            <Parameter label={t("search.type.customerOrderNumber")} value={lineItem.customerOrderNumber} />
          </ProductLineGrid.PONumber>
        )}

        <ProductLineGrid.Messages>
          <LineItemMessagesSection messages={[]} isPMA={isPMA} isOptime={isOptime} quantityVisible={quantityVisible} />
        </ProductLineGrid.Messages>
      </ProductLineGrid>

      <OptionalBox>
        {surchargeValue && <IncludedSurchargeAsterisk value={surchargeValue} percent={lineItem.netPrice?.surcharge} />}
        {tariffSurcharge && <TariffSurchargeAsterisk />}
      </OptionalBox>

      {kitAccordionSlot}

      <PartialDeliveriesSection partialDeliveries={lineItem.partialDeliveries} />

      {lineItem.comment && (
        <Parameter
          label={t("wishlistPage.comment.label")}
          value={lineItem.comment}
          slotProps={{ value: { variant: "bodyMedium" } }}
        />
      )}
    </Card>
  );
}
